import { computeAccommodationOfferTotalsView } from 'checkout/lib/utils/accommodation/priceView'
import { buildBundledExperienceTotalsByParentItemId } from 'checkout/lib/utils/accommodation/bundledExperienceTotals'
import { addTotals } from 'checkout/lib/utils/payments/totals'
import { createSelector } from 'reselect'
import { groupBy, sum } from 'lib/array/arrayUtils'
import { isBedbankItem } from 'lib/checkout/checkoutUtils'
import { checkoutWithMemberPrice, getHasLuxPlusOrSubscriptionInCart } from '../view/luxPlusSubscription'
import { checkoutAccommodationOfferView } from '../view/accommodation'
import getExperienceItemsView from 'checkout/selectors/view/itemViews/getExperienceItemsView'
import getTransferItemsView from 'checkout/selectors/view/itemViews/getTransferItemsView'
import { floatify } from 'lib/maths/mathUtils'
import { getIsPostPurchaseTourChangeDates } from 'checkout/selectors/tourV2Selectors'
import getTourV2ItemViews from 'checkout/selectors/view/itemViews/getTourV2ItemViews'

export const getAccommodationTotals = createSelector(
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => checkoutWithMemberPrice(state),
  (state: App.State) => getHasLuxPlusOrSubscriptionInCart(state),
  (state: App.State) => getIsPostPurchaseTourChangeDates(state),
  (state: App.State) => getExperienceItemsView(state),
  (state: App.State) => getTransferItemsView(state),
  (offerViewsWithStatus, checkoutWithMemberPrice, hasLuxPlusOrSubscriptionInCart, isPostPurchaseTourDateChange, experienceItemsView, transferItemsView): App.WithDataStatus<App.Checkout.ItemViewTotals> => {
    const bundledExperienceTotalsByParentItemId = buildBundledExperienceTotalsByParentItemId(experienceItemsView.data, transferItemsView.data)
    const result = offerViewsWithStatus.data.map(view => computeAccommodationOfferTotalsView(view, { checkoutWithMemberPrice, hasLuxPlusOrSubscriptionInCart, isPostPurchaseTourDateChange, bundledExperienceTotalsByParentItemId }))
    return {
      hasRequiredData: offerViewsWithStatus.hasRequiredData,
      data: addTotals(result).data,
    }
  },
)

export const getTourV2Total = createSelector(
  (state: App.State) => getTourV2ItemViews(state),
  (state: App.State) => checkoutWithMemberPrice(state),
  (state: App.State) => getHasLuxPlusOrSubscriptionInCart(state),
  (state: App.State) => getIsPostPurchaseTourChangeDates(state),
  (offerViewsWithStatus, checkoutWithMemberPrice, hasLuxPlusOrSubscriptionInCart, isPostPurchaseTourDateChange): number => {
    const tourOffersView = offerViewsWithStatus.data.map(view => computeAccommodationOfferTotalsView(view, { checkoutWithMemberPrice, hasLuxPlusOrSubscriptionInCart, isPostPurchaseTourDateChange }))
    return sum(tourOffersView, tourOfferView => tourOfferView.data.price)
  },
)

export const getAccommodationTotal = createSelector(
  (state: App.State) => getAccommodationTotals(state),
  (totals): number => {
    const { price = 0, surcharge = 0, otherFees = {} } = totals.data
    return price + surcharge + sum(Object.values(otherFees))
  },
)

export const getDueAtProperty = createSelector(
  (state: App.State) => getAccommodationTotals(state),
  (totals): number => (totals.data.otherFees?.propertyFees || 0) + (totals.data.otherFees?.extraGuestSurcharge || 0),
)

export const getAccommodationPayable = createSelector(
  (state: App.State) => getAccommodationTotals(state),
  (state: App.State) => getDueAtProperty(state),
  (accommodationTotal, dueAtProperty): number => {
    const { price = 0, surcharge = 0, otherFees = {} } = accommodationTotal.data
    return floatify(price + surcharge + sum(Object.values(otherFees)) - dueAtProperty)
  },
)

export const getBedbankItemIdToLeadItemId = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Map<string, string> => {
    const bedbankItems = items.filter(isBedbankItem)
    const groupedBedbankItems = [...groupBy(bedbankItems, item => item.sessionId).values()]
    return new Map(groupedBedbankItems.flatMap(group => group.map(item => [item.itemId, group[0].itemId])))
  },
)
